import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import Layout from "../components/Layout";
import apiClient from "../services/api";
import { useToast } from "../contexts/ToastContext";
import { EmptyState } from "../components/common/EmptyState";
import {
  AlertItem,
  AlertListResponse,
  AlertType,
} from "../types/grants";
import CreateAlertModal from "../components/grants/alerts/CreateAlertModal";
import AlertCard from "../components/grants/alerts/AlertCard";
import NotificationSettingsPanel from "../components/grants/alerts/NotificationSettingsPanel";

/**
 * Page component for managing grant alerts (saved searches).
 * Users can create up to 3 alerts that trigger email notifications.
 *
 * @see docs/specifications/2026-01-14-alerts-notification-service.md
 */
const GrantsAlerts: React.FC = () => {
  const { addToast } = useToast();
  const queryClient = useQueryClient();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingAlert, setEditingAlert] = useState<AlertItem | null>(null);

  // Fetch alerts
  const {
    data,
    isLoading,
    error,
    refetch,
  } = useQuery<AlertListResponse>({
    queryKey: ["grants-alerts"],
    queryFn: async () => {
      const { data } = await apiClient.get("/grants/alerts");
      return data;
    },
  });

  // Delete alert mutation
  const deleteMutation = useMutation({
    mutationFn: async (alertId: string) => {
      await apiClient.delete(`/grants/alerts/${alertId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["grants-alerts"] });
      addToast({ message: "Alert usunięty", type: "success" });
    },
    onError: () => {
      addToast({ message: "Nie udało się usunąć alertu", type: "error" });
    },
  });

  // Toggle alert enabled mutation
  const toggleMutation = useMutation({
    mutationFn: async ({
      alertId,
      isEnabled,
    }: {
      alertId: string;
      isEnabled: boolean;
    }) => {
      await apiClient.put(`/grants/alerts/${alertId}`, { isEnabled });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["grants-alerts"] });
    },
    onError: () => {
      addToast({ message: "Nie udało się zmienić stanu alertu", type: "error" });
    },
  });

  const handleEdit = (alert: AlertItem) => {
    setEditingAlert(alert);
    setIsCreateModalOpen(true);
  };

  const handleDelete = (alertId: string) => {
    if (window.confirm("Czy na pewno chcesz usunąć ten alert?")) {
      deleteMutation.mutate(alertId);
    }
  };

  const handleToggle = (alert: AlertItem) => {
    toggleMutation.mutate({
      alertId: alert.id,
      isEnabled: !alert.isEnabled,
    });
  };

  const handleModalClose = () => {
    setIsCreateModalOpen(false);
    setEditingAlert(null);
  };

  const handleModalSuccess = () => {
    handleModalClose();
    queryClient.invalidateQueries({ queryKey: ["grants-alerts"] });
  };

  const canCreateMore = (data?.remaining ?? 0) > 0;

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between gap-4">
          <div className="text-left">
            <h1 className="text-2xl font-bold text-neutral-900">
              Alerty naborów
            </h1>
            <p className="mt-1 text-sm text-neutral-600">
              Otrzymuj powiadomienia email o nowych i aktualizowanych naborach
            </p>
          </div>
          <button
            onClick={() => setIsCreateModalOpen(true)}
            disabled={!canCreateMore}
            className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
            title={
              canCreateMore
                ? "Utwórz nowy alert"
                : `Osiągnięto limit ${data?.limit ?? 3} alertów`
            }
          >
            Nowy alert
          </button>
        </div>

        {/* Alert limit info box */}
        <div className="rounded-md bg-primary-50 p-4" role="note" aria-label="Informacja o limicie alertów">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-primary-700" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a.75.75 0 000 1.5h.253a.25.25 0 01.244.304l-.459 2.066A1.75 1.75 0 0010.747 15H11a.75.75 0 000-1.5h-.253a.25.25 0 01-.244-.304l.459-2.066A1.75 1.75 0 009.253 9H9z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-primary-800">
                Możesz utworzyć maksymalnie <strong>{data?.limit ?? 3} alerty</strong>. Każdy alert może monitorować dowolną kombinację filtrów i typów zdarzeń.
                {data && (
                  <span className="ml-1">
                    Wykorzystane: <strong>{data.alerts.length} z {data.limit}</strong>
                    {!canCreateMore && <span className="text-amber-700 font-medium"> (osiągnięto limit)</span>}
                  </span>
                )}
              </p>
            </div>
          </div>
        </div>

        {/* Main content */}
        <div className="bg-white rounded-lg shadow-sm border border-neutral-200">
          {isLoading ? (
            <div className="p-6 text-center" role="status" aria-live="polite">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
              <p className="mt-2 text-sm text-neutral-600">
                Ładowanie alertów...
              </p>
            </div>
          ) : error ? (
            <div className="p-6 text-center" role="alert">
              <div className="text-red-600 mb-4">
                <svg
                  className="h-12 w-12 mx-auto mb-2"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
                <p className="font-medium">Błąd podczas pobierania alertów</p>
              </div>
              <button
                onClick={() => refetch()}
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700"
              >
                Spróbuj ponownie
              </button>
            </div>
          ) : data?.alerts.length === 0 ? (
            <EmptyState
              icon={
                <svg
                  className="mx-auto h-12 w-12"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1}
                    d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                  />
                </svg>
              }
              title="Brak alertów"
              description="Utwórz swój pierwszy alert, aby otrzymywać powiadomienia o nowych i aktualizowanych naborach."
              action={{
                label: "Utwórz alert",
                onClick: () => setIsCreateModalOpen(true),
              }}
            />
          ) : (
            <div className="divide-y divide-neutral-200">
              {data?.alerts.map((alert) => (
                <AlertCard
                  key={alert.id}
                  alert={alert}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  onToggle={handleToggle}
                  isToggling={toggleMutation.isPending}
                  isDeleting={deleteMutation.isPending}
                />
              ))}
            </div>
          )}
        </div>

        {/* Notification Settings */}
        <NotificationSettingsPanel />

        {/* Create/Edit Modal */}
        <CreateAlertModal
          isOpen={isCreateModalOpen}
          onClose={handleModalClose}
          onSuccess={handleModalSuccess}
          editingAlert={editingAlert}
          digestRemaining={data?.digestRemaining}
          hasActiveDigest={(data?.alerts ?? []).some(
            (a) => a.isEnabled && a.type === AlertType.UPCOMING_DIGEST,
          )}
        />
      </div>
    </Layout>
  );
};

export default GrantsAlerts;
